import { createAsyncThunk } from "@reduxjs/toolkit";
import userAuthApi from "../api/userAuthApi";
import {
  UserCompleteRegisterDataType,
  UserForgotPasswordDataType,
  UserSetPasswordDataType,
  UserSignInBodyType,
  UserSignUpBodyType,
  UserSignInResponse,
} from "../types/userAuthTypes.tsx";
import { displayAlert } from "../slice/alertSlice.tsx";
import { getStorageName } from "../../helpers/storageName/storageName.ts";
import { AxiosError } from "axios";
import { handleThunkError } from "./handleThunkError";
import { fetchBrandList } from "./brandThunk.tsx";
import { setNewCurrentBrand } from "../slice/brandSlice.tsx";
import {
  deleteRevmaticsCookie,
  setRevmaticsCookie,
} from "../../helpers/cookie/revmaticsCookies.ts";
import { clearRedirectCounter } from "../../auth/redirectToSignIn";
import { getCookieName } from "../../helpers/cookieName/cookieName.ts";

// Helper function to read cookies
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

interface ApiError {
  detail: string;
}

export const signInUser = createAsyncThunk<
  UserSignInResponse,
  UserSignInBodyType
>("userAuth/signInUser", async (data: UserSignInBodyType, thunkAPI) => {
  try {
    const response = await userAuthApi.userSignIn(data);
    if (response.status === 200) {
      localStorage.setItem(getStorageName.accessToken, response.data.access);
      localStorage.setItem(getStorageName.refreshToken, response.data.refresh);
      const brand = response.data.current_brand.toString();
      thunkAPI.dispatch(setNewCurrentBrand(brand));
      if (brand) {
        setRevmaticsCookie(
          getCookieName.currentBrand,
          response.data.current_brand.toString(),
        );
      } else {
        deleteRevmaticsCookie(getCookieName.currentBrand);
      }

      thunkAPI.dispatch(fetchBrandList());

      // Write shared SSO cookie for cross-application authentication
      // Platform-core should have already set this cookie, but ensure it exists
      const existingSSOCookie = getCookie("revmatics_sso_token");
      if (!existingSSOCookie) {
        // If platform-core didn't set the cookie, use the Django JWT as fallback
        document.cookie = `revmatics_sso_token=${response.data.access}; domain=.revmatics.ai; path=/; secure; max-age=900`;
      }
    }
    const returnPayload: UserSignInResponse = {
      access: response.data.access,
      refresh: response.data.refresh,
      is_first_login: response.data.is_first_login,
      current_brand: response.data.current_brand,
      company_id: response.data.company_id,
      requires_password_reset: response.data.requires_password_reset,
      enable_quickstart: response.data.enable_quickstart,
      has_completed_onboarding: response.data.has_completed_onboarding,
    };
    return returnPayload;
  } catch (e: unknown) {
    let errorDetail = "An unknown error occurred during sign-in.";
    if (e instanceof AxiosError && e.response?.data?.detail) {
      errorDetail = (e.response.data as ApiError).detail;
    } else if (e instanceof Error) {
      errorDetail = e.message;
    }
    return thunkAPI.rejectWithValue(errorDetail);
  }
});

export const signInWithSSOCookieThunk = createAsyncThunk<
  UserSignInResponse,
  string
>("userAuth/signInWithSSO", async (ssoToken, thunkAPI) => {
  try {
    const response = await userAuthApi.signInWithSSOCookie(ssoToken);
    if (response.status === 200) {
      localStorage.setItem(getStorageName.accessToken, response.data.access);
      localStorage.setItem(getStorageName.refreshToken, response.data.refresh);
      const brand = response.data.current_brand.toString();
      thunkAPI.dispatch(setNewCurrentBrand(brand));
      if (brand) {
        setRevmaticsCookie(
          getCookieName.currentBrand,
          response.data.current_brand.toString(),
        );
      } else {
        deleteRevmaticsCookie(getCookieName.currentBrand);
      }

      thunkAPI.dispatch(fetchBrandList());
    }
    const returnPayload: UserSignInResponse = {
      access: response.data.access,
      refresh: response.data.refresh,
      is_first_login: response.data.is_first_login,
      current_brand: response.data.current_brand,
      company_id: response.data.company_id,
      requires_password_reset: response.data.requires_password_reset,
      enable_quickstart: response.data.enable_quickstart,
      has_completed_onboarding: response.data.has_completed_onboarding,
    };
    return returnPayload;
  } catch (e: unknown) {
    let errorDetail = "An unknown error occurred during SSO sign-in.";
    if (e instanceof AxiosError && e.response?.data?.detail) {
      errorDetail = (e.response.data as ApiError).detail;
    } else if (e instanceof Error) {
      errorDetail = e.message;
    }
    // Fail silently
    return thunkAPI.rejectWithValue(errorDetail);
  }
});

// TODO: when API is ready, replace the simulating with the real call.
export const signUpUser = createAsyncThunk(
  "userAuth/signUpUser",
  async (_data: UserSignUpBodyType, thunkAPI) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Simulate a successful response (change to false to simulate an error)
    const shouldSucceed = true;

    if (shouldSucceed) {
      const mockUserData = {
        access: "mock_access_token_on_signup_success",
        refresh: "mock_refresh_token_on_signup_success",
      };
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Account created successfully! Please sign in.",
        }),
      );
      return mockUserData;
    } else {
      const errorDetail =
        "This email is already registered. Please try another.";
      console.warn("[signUpUser Thunk] Simulated error:", errorDetail);
      return thunkAPI.rejectWithValue(errorDetail);
    }
  },
);

/**
 * Asynchronous thunk action for signing out the current user.
 *
 * This function handles the user sign-out process by calling the logout endpoint.
 * The corresponding `extraReducer` in the slice will handle clearing client-side state and storage.
 */
export const signOutUser = createAsyncThunk(
  "userAuth/signOutUser",
  async () => {
    try {
      const response = await userAuthApi.userSignOut();
      localStorage.removeItem(getStorageName.accessToken);
      localStorage.removeItem(getStorageName.refreshToken);
      localStorage.removeItem(getStorageName.currentBrand);
      localStorage.removeItem(getStorageName.currentOrganization);
      deleteRevmaticsCookie(getCookieName.currentBrand);
      deleteRevmaticsCookie(getCookieName.currentOrganization);
      deleteRevmaticsCookie("revmatics_sso_token");
      clearRedirectCounter();
      // Visit Keycloak's end_session_endpoint so the shared SSO
      // session is actually terminated — without this step audi/CRO/
      // launchpad silently re-auth on the next request.
      if (response?.data?.logout_url) {
        window.location.href = response.data.logout_url;
        return;
      }
      window.location.href = "/signin";
    } catch (error) {
      console.error(
        "Server logout failed, but proceeding with client-side cleanup:",
        error,
      );
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "userAuth/forgotPassword",
  async (data: UserForgotPasswordDataType, thunkAPI) => {
    try {
      const response = await userAuthApi.forgotPassword(data.body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Please check your email inbox and follow the instructions to reset your password.",
          heading: response.data.message,
        }),
      );
      if (data.callback) {
        data.callback();
      }
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unknown error occurred during enter email.",
      );
      return thunkAPI.rejectWithValue(
        "An unknown error occurred during enter email.",
      );
    }
  },
);

export const setPassword = createAsyncThunk(
  "userAuth/setPassword",
  async (data: UserSetPasswordDataType, thunkAPI) => {
    try {
      const response = await userAuthApi.setPassword(data.body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          heading: "New password saved!",
          text: "You’ll now be redirected to login page to sign in.",
        }),
      );
      if (data.callback) {
        data.callback();
      }
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unknown error occurred during enter email.",
      );
      return thunkAPI.rejectWithValue(
        "An unknown error occurred during enter email.",
      );
    }
  },
);

export const registerCompleteUser = createAsyncThunk(
  "userAuth/registerCompleteUser",
  async (data: UserCompleteRegisterDataType, thunkAPI) => {
    try {
      const response = await userAuthApi.registerCompleteUser(data.body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Account created successfully! Please sign in.",
        }),
      );
      if (data.callback) {
        data.callback();
      }
      return response.data.access;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unknown error occurred during registration.",
      );
      return thunkAPI.rejectWithValue(
        "An unknown error occurred during registration.",
      );
    }
  },
);
